import { baseApi, defaultQueryFreshness } from "./baseApi";
import type { CreateTradeOfferMultipartPayload, TradeOfferRequestOptions } from "../../api/tradeOffers";

export const tradeOffersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createTradeOffer: builder.mutation<unknown, { payload: Record<string, unknown>; options?: TradeOfferRequestOptions }>({
      query: ({ payload, options }) => {
        const headers = options?.idempotencyKey ? { "Idempotency-Key": options.idempotencyKey } : undefined;
        return {
          url: "/trade-offers",
          method: "POST",
          data: payload,
          ...(headers ? { headers } : {}),
        };
      },
      invalidatesTags: [{ type: "TradeOffers", id: "LIST" }],
    }),
    createTradeOfferMultipart: builder.mutation<unknown, CreateTradeOfferMultipartPayload>({
      query: (payload) => {
        const formData = new FormData();
        formData.append("fromUserId", payload.fromUserId);
        formData.append("toUserId", payload.toUserId);
        if (payload.tradeId) formData.append("tradeId", payload.tradeId);
        if (payload.bountyId) formData.append("bountyId", payload.bountyId);
        if (payload.offerCardName) formData.append("offerCardName", payload.offerCardName);
        if (payload.offerCardGame) formData.append("offerCardGame", payload.offerCardGame);
        if (payload.offerCardId) formData.append("offerCardId", payload.offerCardId);
        if (payload.offerCardNameCanonical) formData.append("offerCardNameCanonical", payload.offerCardNameCanonical);
        if (payload.offerCardCondition) formData.append("offerCardCondition", payload.offerCardCondition);
        if (payload.offerCardValue !== undefined) formData.append("offerCardValue", String(payload.offerCardValue));
        if (payload.cashAmount !== undefined) formData.append("cashAmount", String(payload.cashAmount));
        if (payload.message) formData.append("message", payload.message);
        if (payload.offerGradingCompany) formData.append("offerGradingCompany", payload.offerGradingCompany);
        if (payload.offerGradeNumber) formData.append("offerGradeNumber", payload.offerGradeNumber);
        if (payload.file) {
          formData.append("file", payload.file, payload.file instanceof File ? payload.file.name : "offer.webp");
        }

        const headers = payload.idempotencyKey ? { "Idempotency-Key": payload.idempotencyKey } : undefined;
        return {
          url: "/trade-offers",
          method: "POST",
          data: formData,
          timeout: 30000,
          ...(headers ? { headers } : {}),
        };
      },
      invalidatesTags: [{ type: "TradeOffers", id: "LIST" }],
    }),
    getTradeOffersForUser: builder.query<unknown[], string>({
      query: (userId) => ({ url: `/trade-offers/user/${userId}`, method: "GET" }),
      providesTags: (_res, _err, userId) => [{ type: "TradeOffers", id: `user:${userId}` }],
      ...defaultQueryFreshness,
    }),
    getTradeOfferById: builder.query<unknown, string>({
      query: (offerId) => ({ url: `/trade-offers/${offerId}`, method: "GET" }),
      providesTags: (_res, _err, offerId) => [{ type: "TradeOffers", id: String(offerId) }],
      ...defaultQueryFreshness,
    }),
    updateTradeOfferStatus: builder.mutation<
      unknown,
      { offerId: string; status: "accepted" | "rejected"; counterOffer?: Record<string, unknown> }
    >({
      query: ({ offerId, status, counterOffer }) => ({
        url: `/trade-offers/${offerId}/status`,
        method: "PATCH",
        data: { status, ...(counterOffer ? { counterOffer } : {}) },
      }),
      invalidatesTags: (_res, _err, { offerId }) => [
        { type: "TradeOffers", id: String(offerId) },
        { type: "TradeOffers", id: "LIST" },
      ],
    }),
    cancelTradeOffer: builder.mutation<unknown, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/trade-offers/${offerId}/cancel`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, { offerId }) => [
        { type: "TradeOffers", id: String(offerId) },
        { type: "TradeOffers", id: "LIST" },
      ],
    }),
    updateTradeOfferMeetup: builder.mutation<
      unknown,
      { offerId: string; payload: { location: string; dateTime: string; confirm?: boolean } }
    >({
      query: ({ offerId, payload }) => ({
        url: `/trade-offers/${offerId}/meetup`,
        method: "PATCH",
        data: payload,
      }),
      invalidatesTags: (_res, _err, { offerId }) => [{ type: "TradeOffers", id: String(offerId) }],
    }),
    confirmTradeOfferMeetup: builder.mutation<unknown, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/trade-offers/${offerId}/meetup-confirm`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, { offerId }) => [{ type: "TradeOffers", id: String(offerId) }],
    }),
    confirmTradeOfferExchange: builder.mutation<unknown, { offerId: string }>({
      query: ({ offerId }) => ({
        url: `/trade-offers/${offerId}/exchange-confirm`,
        method: "POST",
      }),
      invalidatesTags: (_res, _err, { offerId }) => [{ type: "TradeOffers", id: String(offerId) }],
    }),
    submitTradeOfferReview: builder.mutation<unknown, { offerId: string; payload: { rating: number; tags: string[] } }>({
      query: ({ offerId, payload }) => ({
        url: `/trade-offers/${offerId}/reviews`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: (_res, _err, { offerId }) => [{ type: "TradeOffers", id: String(offerId) }],
    }),
  }),
});

export const {
  useCreateTradeOfferMutation,
  useCreateTradeOfferMultipartMutation,
  useGetTradeOffersForUserQuery,
  useGetTradeOfferByIdQuery,
  useUpdateTradeOfferStatusMutation,
  useCancelTradeOfferMutation,
  useUpdateTradeOfferMeetupMutation,
  useConfirmTradeOfferMeetupMutation,
  useConfirmTradeOfferExchangeMutation,
  useSubmitTradeOfferReviewMutation,
} = tradeOffersApi;
